import { z } from 'zod';
import { dotSchema } from './dot.js';
import { snapshotSchema } from './snapshot.js';

/**
 * A hill chart - exactly one per upstream project (= a Shape Up Pitch).
 *
 * Design choice (validated): we keep a 1-to-1 mapping project to hill rather
 * than a hill per cycle. For a team of 6 this is the most readable; per-cycle
 * rollups can be derived client-side if ever needed.
 *
 * `provider` records which upstream system backs this hill. Rows with
 * `provider: 'linear'` are legacy hills created before the GitLab migration -
 * their sync is disabled and they are kept read-only for history. New hills
 * are always `provider: 'gitlab'`.
 *
 * `title` is the user-owned display name of the hill (the pitch name),
 * initialised from the upstream project name at import time and then freely
 * editable. `externalProjectName` stays the upstream mirror and is never
 * touched by that edit.
 *
 * Lifecycle fields:
 *  - `completedAt` / `completedByUserId` flip together when a user clicks
 *    "Mark as Done". Both NULL means the hill is Active; both non-NULL means
 *    Done (read-only, archived, no live mode). The transition is irreversible.
 *  - `externalProjectDeletedAt` is independent: when set, the upstream project
 *    backing this hill has been removed upstream. We surface this as an
 *    "orphaned" badge on Active hills; it has no effect on Done hills.
 */
export const hillChartSchema = z.object({
  id: z.string().uuid(),
  provider: z.enum(['linear', 'gitlab']),
  title: z.string(),
  externalProjectId: z.string(),
  externalProjectName: z.string(),
  externalProjectUrl: z.string().url().nullable(),
  createdByUserId: z.string().uuid(),
  createdAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
  completedByUserId: z.string().uuid().nullable(),
  externalProjectDeletedAt: z.string().datetime().nullable(),
  dots: z.array(dotSchema),
});
export type HillChart = z.infer<typeof hillChartSchema>;

/**
 * An upstream project that can be imported into a hill chart.
 *
 * Returned by `GET /api/gitlab/projects` to populate the "create hill" picker:
 * the GitLab projects the authenticated user is a member of, with their
 * milestone count. `alreadyImported` lets the UI grey out projects that
 * already have a hill (one-to-one invariant); it reflects `provider: 'gitlab'`
 * hills only, since a legacy Linear project id could otherwise shadow an
 * unrelated GitLab project that happens to share it.
 */
export const externalProjectSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  url: z.string().url().nullable(),
  state: z.string().nullable(),
  milestoneCount: z.number().int().nonnegative(),
  alreadyImported: z.boolean(),
});
export type ExternalProjectSummary = z.infer<typeof externalProjectSummarySchema>;

/**
 * Response envelopes for the list endpoints. Wrapping in an object (rather
 * than returning a bare array) leaves room to add pagination / metadata later
 * without a breaking change on the wire.
 */
export const hillChartListResponseSchema = z.object({
  hills: z.array(hillChartSchema),
});
export type HillChartListResponse = z.infer<typeof hillChartListResponseSchema>;

export const externalProjectsListResponseSchema = z.object({
  projects: z.array(externalProjectSummarySchema),
});
export type ExternalProjectsListResponse = z.infer<typeof externalProjectsListResponseSchema>;

export const snapshotsListResponseSchema = z.object({
  snapshots: z.array(snapshotSchema),
});
export type SnapshotsListResponse = z.infer<typeof snapshotsListResponseSchema>;
